import { useId } from 'react';

/**
 * A arte do correio em vetor (techspec-correio §06): a carta fechada com o lacre de cera, a carta aberta, a encomenda
 * e a caixa vazia. Vetor porque a carta flutuante cresce no hover e o ícone pixelado do protótipo borrava.
 * Os ids dos gradientes vêm do useId (sem dois-pontos), um por desenho na tela.
 */
const useIds = () => useId().replace(/:/g, '');

/** O lacre de cera vermelho com a estrela em relevo. `x`, `y` é o centro. */
function Lacre({ id, x, y, r }: { id: string; x: number; y: number; r: number }) {
  const s = r / 8;
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <path d="M-3 6 -6 15 -2.5 12.5 0 16 1 7z" fill="#8f1f17" stroke="#4a0d08" strokeWidth=".6" strokeLinejoin="round" />
      <path d="M3 6 6.5 14.5 2.6 12.6 0.6 16 -0.6 7z" fill="#a8281e" stroke="#4a0d08" strokeWidth=".6" strokeLinejoin="round" />
      <path
        d="M0-8.6c1.4 0 2 1 3.3 1.3 1.4.3 2.6-.3 3.5.6.9.9.3 2.2.7 3.5.4 1.3 1.4 2 1.4 3.3s-1 2-1.4 3.3c-.4 1.3.2 2.6-.7 3.5-.9.9-2.1.3-3.5.6C2 7.4 1.4 8.6 0 8.6s-2-1.2-3.3-1.5c-1.4-.3-2.6.3-3.5-.6-.9-.9-.3-2.2-.7-3.5C-7.9 1.7-8.6 1.2-8.6 0s.7-1.7 1.1-3c.4-1.3-.2-2.6.7-3.5.9-.9 2.1-.3 3.5-.6C-2-7.4-1.4-8.6 0-8.6z"
        fill={`url(#${id}-cera)`} stroke="#4a0d08" strokeWidth=".7"
      />
      <circle r="5.4" fill="none" stroke="#5e130d" strokeWidth=".9" opacity=".75" />
      <circle r="5.4" fill="none" stroke="#e98a78" strokeWidth=".45" opacity=".55" transform="translate(-.4 -.5)" />
      <path d="M0-3.6 1 -1.1 3.5-1 1.5.6 2.2 3.1 0 1.7-2.2 3.1-1.5.6-3.5-1-1-1.1z" fill="#6d150f" stroke="#f09a86" strokeWidth=".35" strokeOpacity=".6" />
      <ellipse cx="-3" cy="-4.4" rx="2.2" ry="1.1" fill="#fff" opacity=".28" transform="rotate(-30 -3 -4.4)" />
    </g>
  );
}

function GradientesCera({ id }: { id: string }) {
  return (
    <radialGradient id={`${id}-cera`} cx="38%" cy="32%" r="75%">
      <stop offset="0" stopColor="#e2685a" />
      <stop offset=".55" stopColor="#a8281e" />
      <stop offset="1" stopColor="#5c120c" />
    </radialGradient>
  );
}

function GradientesPapel({ id }: { id: string }) {
  return (
    <>
      <linearGradient id={`${id}-papel`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#f6ebcf" />
        <stop offset="1" stopColor="#dcc591" />
      </linearGradient>
      <linearGradient id={`${id}-aba`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#e9d7a8" />
        <stop offset="1" stopColor="#c9ad72" />
      </linearGradient>
      <linearGradient id={`${id}-fundo`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ecdcb1" />
        <stop offset="1" stopColor="#d2b77d" />
      </linearGradient>
    </>
  );
}

/** A carta fechada com o lacre: a carta flutuante e a linha da carta nova. */
export function CartaFechada({ className }: { className?: string }) {
  const id = useIds();
  return (
    <svg viewBox="0 0 64 48" className={className} aria-hidden="true">
      <defs>
        <GradientesPapel id={id} />
        <GradientesCera id={id} />
      </defs>
      <rect x="2.5" y="4.5" width="59" height="39" rx="3" fill={`url(#${id}-papel)`} stroke="#5e4219" strokeWidth="1.2" />
      {/* as abas de baixo e dos lados */}
      <path d="M3.5 42.5 25 25.5M60.5 42.5 39 25.5" fill="none" stroke="#a88a52" strokeWidth="1" strokeLinecap="round" />
      <path d="M4 42 32 26l28 16z" fill={`url(#${id}-fundo)`} opacity=".55" />
      {/* a aba de cima */}
      <path d="M3 5.5 32 29 61 5.5z" fill={`url(#${id}-aba)`} stroke="#5e4219" strokeWidth="1.1" strokeLinejoin="round" />
      <path d="M6 6.5 32 27.5" stroke="#fff6dc" strokeWidth=".8" opacity=".55" />
      {/* a borda gasta do pergaminho */}
      <rect x="4" y="6" width="56" height="36" rx="2" fill="none" stroke="#fff3d0" strokeWidth=".6" opacity=".45" />
      <Lacre id={id} x={32} y={27.5} r={8.2} />
    </svg>
  );
}

/** A carta já lida: aberta, com a folha para fora. */
export function CartaAberta({ className, vazia = false }: { className?: string; vazia?: boolean }) {
  const id = useIds();
  return (
    <svg viewBox="0 0 64 56" className={className} aria-hidden="true">
      <defs>
        <GradientesPapel id={id} />
        <linearGradient id={`${id}-folha`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fffaf0" />
          <stop offset="1" stopColor="#ece0c2" />
        </linearGradient>
      </defs>
      {/* a aba aberta, para cima */}
      <path d="M3 22.5 32 3 61 22.5z" fill={`url(#${id}-aba)`} stroke="#5e4219" strokeWidth="1.1" strokeLinejoin="round" />
      {!vazia && (
        <g>
          <rect x="11" y="9" width="42" height="30" rx="1.5" fill={`url(#${id}-folha)`} stroke="#8a7146" strokeWidth=".9" />
          <path d="M16 15.5h22M16 20h32M16 24.5h28M16 29h24" stroke="#8a7146" strokeWidth="1.3" strokeLinecap="round" opacity=".7" />
        </g>
      )}
      <rect x="2.5" y="22.5" width="59" height="31" rx="3" fill={`url(#${id}-papel)`} stroke="#5e4219" strokeWidth="1.2" />
      <path d="M3.5 23.5 32 41 60.5 23.5" fill="none" stroke="#a88a52" strokeWidth="1.1" strokeLinejoin="round" />
      <path d="M4 53 25 36.5M60 53 39 36.5" fill="none" stroke="#a88a52" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

/** A carta com anexo esperando: o pacote de papel pardo com o barbante, a etiqueta e o lacre. */
export function Encomenda({ className }: { className?: string }) {
  const id = useIds();
  return (
    <svg viewBox="0 0 64 56" className={className} aria-hidden="true">
      <defs>
        <GradientesCera id={id} />
        <linearGradient id={`${id}-tampa`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#d9a866" />
          <stop offset="1" stopColor="#b9874a" />
        </linearGradient>
        <linearGradient id={`${id}-frente`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#c48f50" />
          <stop offset="1" stopColor="#966331" />
        </linearGradient>
        <linearGradient id={`${id}-lado`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#8c5a2b" />
          <stop offset="1" stopColor="#6e4420" />
        </linearGradient>
      </defs>
      {/* a caixa em perspectiva */}
      <path d="M6 18 24 9h34L40 18z" fill={`url(#${id}-tampa)`} stroke="#4f3214" strokeWidth="1.1" strokeLinejoin="round" />
      <path d="M40 18 58 9v29L40 49z" fill={`url(#${id}-lado)`} stroke="#4f3214" strokeWidth="1.1" strokeLinejoin="round" />
      <rect x="6" y="18" width="34" height="31" fill={`url(#${id}-frente)`} stroke="#4f3214" strokeWidth="1.1" strokeLinejoin="round" />
      {/* o barbante */}
      <path d="M23 49V18l18-9M15 13.5h34v30" fill="none" stroke="#efe2c0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M23 49V18l18-9M15 13.5h34v30" fill="none" stroke="#a48c5f" strokeWidth=".6" strokeLinejoin="round" />
      <path d="M6 33.5h34l18-9" fill="none" stroke="#efe2c0" strokeWidth="2" strokeLinejoin="round" />
      <path d="M6 33.5h34l18-9" fill="none" stroke="#a48c5f" strokeWidth=".6" strokeLinejoin="round" />
      {/* a etiqueta */}
      <g transform="rotate(-8 14 41)">
        <path d="M8 37h12l3 3.5-3 3.5H8z" fill="#f4e8c8" stroke="#5e4219" strokeWidth=".9" strokeLinejoin="round" />
        <circle cx="19.6" cy="40.5" r=".9" fill="#5e4219" />
        <path d="M10 39.6h7M10 41.6h5" stroke="#8a7146" strokeWidth=".8" strokeLinecap="round" />
      </g>
      <Lacre id={id} x={23} y={33.5} r={6.4} />
    </svg>
  );
}

/** A janela sem carta escolhida ou a caixa vazia: a carta aberta, sem folha. */
export function CaixaVazia({ className }: { className?: string }) {
  return <CartaAberta className={className} vazia />;
}

/** O ícone de traço do correio (título da janela, linha do Mais). */
export function IconeCorreio({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3.5 6.5 8.5 6.5 8.5-6.5" />
    </svg>
  );
}
